
// Fonction intercepteur
import {HttpClient, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse} from '@angular/common/http';
import {firstValueFrom, Observable} from 'rxjs';
import {inject} from '@angular/core';
import {AppAuthService} from '../services/app.auth.service';
import {environment} from '../../../environments/environment';
import {Router} from '@angular/router';
import {AppCacheService} from '../services/app.cache.service';
import {AppSessionService} from '../services/app.session.service';
import {AppToasterService} from '../services/app.toaster.service';
import {tap} from 'rxjs/operators';
import {AppGlobalEventManagerService} from '../services/app.globalEventManager.service';
import {LoopBackConfig} from '../../shared/sdk';
import {Arbitre401} from './arbitre401.utils';

/**
 * Marqueur de l'appel de VÉRIFICATION de session : quand cet appel échoue
 * lui-même en 401, l'intercepteur ne doit pas ré-arbitrer (récursion) — c'est
 * l'arbitre qui interprète cet échec comme « session morte ».
 */
const ENTETE_VERIFICATION = 'X-Verification-Session';

/**
 * Arbitre partagé par TOUTES les requêtes (état de module, pas d'instance par
 * appel) : c'est lui qui garantit qu'une rafale de 401 simultanés — pollers,
 * chargements parallèles d'une fiche — ne déclenche qu'une seule vérification
 * et une seule action. Voir arbitre401.utils.ts.
 */
let arbitre: Arbitre401 | null = null;

export function addHeaderInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const auth = inject(AppAuthService);
  const globalEventManagerService = inject(AppGlobalEventManagerService);
  const token = auth.getAccessTokenId();
  const router = inject(Router);
  const cacheService = inject(AppCacheService);
  const sessionService = inject(AppSessionService);
  const toastr = inject(AppToasterService);
  const http = inject(HttpClient);

  // Récupérer la valeur actuelle directement depuis le BehaviorSubject
  const apiParameters = globalEventManagerService._apiParameters.value;

  const clonedRequest = req.clone({
    headers: req.headers.set('Authorization', token || '').set('app', environment?.header)
  });

  /** Purge complète de la session — l'ancien comportement, désormais réservé aux sessions réellement mortes. */
  const deconnecter = () => {
    auth.clear();
    cacheService.clearAllCaches();
    localStorage.removeItem('$VGE$impersonation');
    localStorage.removeItem('sinistreTimer:context');
    localStorage.removeItem('sinistreTimer:visible');
    sessionService.updateLogguedAccount(null);
    toastr.warning('Votre session a expirée, veuillez vous reconnecter.', 'Session expirée');
    try {
      globalEventManagerService.closeSpinner();
    } catch (e) {
      console.log('Error while closing spinner', e);
    }
    router.navigate(['/login']);
  };

  /**
   * La session est-elle encore vivante ? Appel léger accessible à TOUT profil
   * authentifié (People.sessionValide, ACL $authenticated). Marqué pour que son
   * propre 401 ne ré-entre pas dans l'arbitrage.
   */
  const verifierSession = (): Promise<boolean> => {
    const url = LoopBackConfig.getPath() + '/' + LoopBackConfig.getApiVersion() + '/Peoples/sessionValide';
    return firstValueFrom(http.get(url, { headers: { [ENTETE_VERIFICATION]: '1' } }))
      .then(() => true)
      .catch(() => false);
  };

  return next(clonedRequest).pipe(
    tap({
      next: (event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (apiParameters?.TTL) {
            sessionService.updateTTL(apiParameters.TTL);
          }
        }
      },
      error: (error: any) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {

          // L'appel de vérification lui-même : son échec est interprété par
          // l'arbitre (session morte), ne pas ré-arbitrer ici.
          if (req.headers.has(ENTETE_VERIFICATION)) { return; }

          // ⚠ Un 401 ne vaut plus déconnexion immédiate. LoopBack répond 401
          // aussi bien pour un jeton mort que pour un refus d'ACL sur une
          // session valide — déconnecter sans distinguer éjectait des
          // utilisateurs en plein travail (incident ULTRAnet du 31/07/2026).
          // L'arbitre vérifie la session UNE fois par rafale ; seul le premier
          // signalement porte les effets de bord.
          console.log('401 by interceptor !', req.url);
          if (!arbitre) { arbitre = new Arbitre401(verifierSession); }
          arbitre.signaler401().then(({ verdict, premier }) => {
            if (!premier) { return; }
            if (verdict === 'deconnecter') {
              deconnecter();
            } else {
              // Session vivante : simple refus d'accès. L'utilisateur reste
              // connecté et sait pourquoi l'écran n'a pas répondu.
              toastr.warning('Votre profil n\'a pas accès à cette ressource.', 'Accès non autorisé');
            }
          });
        }
      }
    })
  );
}
